import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import {
	addToList,
	fetchPriorityListById,
	removeFromList,
	removePriorityListsByIds,
	replaceIDAtIndex,
	selectDeletingPriorityList,
	selectPriorityListsById,
	setPriorityListObjects,
	updatePriorityListActiveFor,
	updatePriorityListDescription,
	updatePriorityListName,
} from "./slice";
import { fetchPasses } from "../passes/slice";
import { fetchRSOById, selectRSOsById } from "../rsos/slice";
import NavMain from "../../app/NavMain";
import Layout from "../../ui/Layout";
import ScrollablePage from "../../ui/ScrollablePage";
import ErrorMsg from "../../ui/ErrorMsg";
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner";
import DeleteConfirmationDialog from "../../ui/DeleteConfirmationDialog";
import { Link } from "react-router-dom";
import { selectAdvancedMode } from "../settings/slice";
import Timestamp from "../../ui/Timestamp";
import {
	faGlobe,
	faMinus,
	faPlay,
	faSave,
	faUndo,
} from "@fortawesome/free-solid-svg-icons";

import { Action, Actions, Facts, Header, Overview } from "../../ui/PageHeader";
import Breadcrumbs from "../../ui/Breadcrumbs";
import IDCopy from "../../ui/IDCopy";
import Button from "../../ui/Button";
import UserEditable from "../../ui/UserEditable";
import ActionsFooter from "../../ui/ActionsFooter";
import CompactSelector from "../astrosites/CompactSelector";
import Table, { Row, Cell } from "../../ui/Table";
import TableHeaderRow from "../../ui/TableHeaderRow";
import TableHeader from "../../ui/TableHeader";
import Input from "../../ui/Input";
import { calculateOrbitType } from "../rsos/orbit";
import ID from "../../ui/ID";
import Text from "../../ui/Text";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import IconButton from "../../ui/IconButton";
import { parseIDs } from "../rsos/parse";
import GeneratePasses from "../passes/GeneratePasses";
import Authed from "../iam/Authed";

const PriorityList = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const prioritiesByID = useSelector(selectPriorityListsById);
	const { id } = useParams();
	const inProgressDeletions = useSelector((state) =>
		selectDeletingPriorityList(state),
	);
	const priority = prioritiesByID[id];
	var status = priority?._loadStatus;
	if (
		(status == "idle" &&
			priority?._saveStatus &&
			priority?._saveStatus != "idle") ||
		!status
	) {
		status = priority?._saveStatus;
	}
	const advancedMode = useSelector(selectAdvancedMode);

	// Managing of selected RSOs - for deletion etc
	const [selected, setSelected] = useState(new Set());
	const toggleSelectAll = () => {
		if (selected.size < priority?.norad_ids?.length) {
			setSelected(new Set(priority.norad_ids));
		} else {
			setSelected(new Set());
		}
	};
	const isSelectAll = selected.size == priority?.norad_ids?.length;
	const toggleSelected = (id) => () => {
		if (selected.has(id)) {
			selected.delete(id);
		} else {
			selected.add(id);
		}
		setSelected(new Set(selected));
	};

	useEffect(() => {
		dispatch(fetchPriorityListById(id));
	}, [id]);

	const onDelete = async () => {
		// TODO: Show loading spinner / flag
		try {
			await dispatch(removePriorityListsByIds([id]));
			navigate({
				pathname: "/priorities",
				search: "?sort_by=priority&sort_order=DESC",
			});
		} catch (failed) {
			// FIXME: Show failure message etc
		}
	};
	const onDescriptionChange = (description) => {
		// TODO: Upon failure, this does not Promise.reject / throw exception,
		// so UserEditable cannot show failure status. Discuss.
		return dispatch(
			updatePriorityListDescription({
				priority_id: priority.id,
				_etag: priority._etag,
				description,
			}),
		);
	};
	const onNameChange = (name) => {
		// TODO: Upon failure, this does not Promise.reject / throw exception,
		// so UserEditable cannot show failure status. Discuss.
		return dispatch(
			updatePriorityListName({
				priority_id: priority.id,
				_etag: priority._etag,
				name,
			}),
		);
	};
	const handleSetActiveFor = (priority_id, _etag) => (astrosite_ids) => {
		dispatch(
			updatePriorityListActiveFor({ priority_id, astrosite_ids, _etag }),
		);
		setSelected(new Set()); // Reset selection
	};

	const handleRemoveSelected = () => {
		dispatch(removeFromList({ listID: id, itemIDs: Array.from(selected) }));
		setSelected(new Set()); // Reset selection otherwise need to remove from selection
	};

	const handleRemoveSingle = (rsoID) => () => {
		dispatch(removeFromList({ listID: id, itemIDs: [rsoID] }));
		setSelected(new Set()); // Reset selection otherwise need to update selection
	};

	const handleReplaceSingle = (index) => (newItemID) => {
		dispatch(replaceIDAtIndex({ listID: id, index, newItemID }));
		setSelected(new Set()); // Reset selection otherwise need to update selection
	};

	// Adding new item(s)
	const [adding, setAdding] = useState("");
	const handleAdd = () => {
		setAdding("");
		// Note: Action creator will split + sanitise value
		dispatch(addToList({ listID: id, idString: adding }));
	};

	// Save changes or reset
	const handleSaveChanges = () => {
		dispatch(
			setPriorityListObjects({
				priority_id: id,
				norad_ids: priority.norad_ids,
				_etag: priority._etag,
			}),
		);
	};

	const handleUndoChanges = () => {
		dispatch(fetchPriorityListById(id));
	};

	const navTo = (id) => () => navigate(`/rsos/${id}`);

	const [isRegeneratingPasses, setIsRegeneratingPasses] = useState(false);

	const handleOpenRegeneratePasses = () => {
		setIsRegeneratingPasses(!isRegeneratingPasses);
	};

	const handleCloseRegeneratePasses = () => {
		setIsRegeneratingPasses(false);
	};

	const handleAfterGeneration = () => {
		const now = new Date();
		const to = new Date(now.getTime());
		to.setUTCDate(to.getUTCDate() + 2);
		dispatch(
			fetchPasses({
				filter: {
					object_ids: [id],
					from: now.toISOString(),
					to: to.toISOString(),
					sort_by: "start_date",
					sort_order: "ASC",
				},
				page: {
					offset: 0,
					limit: 20,
				},
				context: `rso/${id}`,
			}),
		);
	};

	return (
		<Layout>
			<NavMain />
			<Helmet>
				<title>Priority list: {priority?.name || id} - Astrosite</title>
			</Helmet>
			<ScrollablePage>
				{(status === "loading" || status === "updating") && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
				{inProgressDeletions?._saveStatus === "deleting" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
				<Header>
					<Overview>
						<UserEditable
							tagName="h1"
							value={priority?.name}
							onChange={onNameChange}
							disabled={status != "idle"}
						/>
						<Breadcrumbs>
							<Link to="/priorities?sort_by=priority&sort_order=DESC">
								Priority lists
							</Link>
							<IDCopy id={id} showCopy />
						</Breadcrumbs>
						<UserEditable
							tagName="p"
							value={priority?.description}
							onChange={onDescriptionChange}
							disabled={status != "idle"}
						/>
					</Overview>
					<Facts>
						<dt>Objects</dt>
						<dd>{priority?.norad_ids?.length || "-"}</dd>
						<dt>Priority</dt>
						<dd>{priority?.priority || "-"}</dd>
						<dt>Active for</dt>
						<dd>
							{status != "loading" && (
								<CompactSelector
									value={priority?.active_for_astrosites}
									onChange={handleSetActiveFor(priority?.id, priority?._etag)}
								/>
							)}
						</dd>
						<dt>Created</dt>
						<dd>
							{priority?.created_at && (
								<Timestamp utc={advancedMode} columnMode={false}>
									{priority?.created_at}
								</Timestamp>
							)}
						</dd>
					</Facts>
					<Actions>
						{!priority?._modified && (
							<>
								<Action>
									<Link
										to={`/passes?from=now&to=p48h&sort_by=start_date&sort_order=ASC&object_ids=${priority?.norad_ids}`}
										disabled
									>
										<Button icon={faGlobe}>Show passes</Button>
									</Link>
								</Action>
								<Action>
									<Button
										onClick={handleOpenRegeneratePasses}
										icon={faGlobe}
										disabled
									>
										Regenerate passes
									</Button>
								</Action>
								<Authed allowRoles={["deployer"]}>
									<Action>
										<DeleteConfirmationDialog
											text="Delete this list"
											item={{ name: priority?.name }}
											onDelete={() => onDelete(priority?.id)}
											disabled
										/>
									</Action>
								</Authed>
								{/* Tasking is hidden for the moment, needs a re-think.
								<Action>
									<Link
										to={"/playlists/deploy-manual" + "?priority_lists=" + id}
									>
										<Button icon={faPlay} new>
											{`Schedule ${priority?.norad_ids?.length || ""} ${
												priority?.norad_ids?.length > 1 ? "objects" : "object"
											} `}
										</Button>
									</Link>
								</Action>
								*/}
							</>
						)}
						{priority?._modified && (
							<>
								<span>Staged changes:</span>
								<Action>
									<Button icon={faUndo} delete onClick={handleUndoChanges}>
										Undo changes
									</Button>
								</Action>{" "}
								<Action>
									<Button icon={faSave} active onClick={handleSaveChanges}>
										Save changes
									</Button>
								</Action>
							</>
						)}
					</Actions>
				</Header>
				{status === "rejected" && (
					<ErrorMsg
						msg={`There is a problem fetching the priority list with ID ${id}`}
						showIcon={true}
					/>
				)}
				{priority?.norad_ids && status == "idle" && (
					<Table>
						<TableHeaderRow sticky>
							<TableHeader>
								<input
									type="checkbox"
									checked={isSelectAll}
									onChange={toggleSelectAll}
								/>
							</TableHeader>
							<TableHeader>#</TableHeader>
							<TableHeader>ID</TableHeader>
							<TableHeader>Name</TableHeader>
							<TableHeader>COSPAR ID</TableHeader>
							<TableHeader>Orbit type</TableHeader>
							<TableHeader>Actions</TableHeader>
						</TableHeaderRow>
						<tbody>
							{priority?.norad_ids?.map((id, i) => (
								<Row key={id} zebra>
									<Cell>
										<input
											type="checkbox"
											checked={selected.has(id)}
											onChange={toggleSelected(id)}
										/>
									</Cell>
									<Cell>{i + 1}.</Cell>
									<Cell>
										<RSOIDInput value={id} onChange={handleReplaceSingle(i)} />
									</Cell>
									<RSOMetaCols id={id} onClick={navTo(id)} />
									<Cell>
										<IconButton
											onClick={handleRemoveSingle(id)}
											secondary
											icon={faTrash}
										/>
									</Cell>
								</Row>
							))}
							<Row>
								<Cell>Add:</Cell>
								<Cell>{priority?.norad_ids?.length + 1}.</Cell>
								<Cell colSpan={4}>
									<Input
										type="text"
										placeholder="One or more NORAD IDs"
										value={adding}
										onChange={(e) => setAdding(e.target.value)}
										onBlur={handleAdd}
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												handleAdd();
											}
										}}
									/>
								</Cell>
							</Row>
						</tbody>
					</Table>
				)}
				{/* TODO: Replace with different view allowing for inline editing and addition
        <PriorityObjects
          priorityId={id}
          rsoIds={priority?.norad_ids}
          selected={selected}
          setSelected={setSelected}
        />
        */}
				{status == "idle" && (
					<ActionsFooter>
						{priority?._modified && (
							<>
								<Text small secondary>
									Your changes are not yet visible to others:&nbsp;&nbsp;&nbsp;
								</Text>
								<Button icon={faSave} active onClick={handleSaveChanges}>
									Save changes
								</Button>

								<Button icon={faUndo} delete onClick={handleUndoChanges}>
									Undo changes
								</Button>
							</>
						)}
						{selected && selected.size > 0 && (
							<Button delete icon={faMinus} onClick={handleRemoveSelected}>
								Remove {selected.length} selected
							</Button>
						)}
					</ActionsFooter>
				)}
			</ScrollablePage>
			{isRegeneratingPasses && (
				<GeneratePasses
					handleCancel={handleCloseRegeneratePasses}
					noradIds={priority?.norad_ids}
					handleComplete={handleAfterGeneration}
				/>
			)}
		</Layout>
	);
};

const RSOMetaCols = ({ id, onClick }) => {
	// Select from state. If preset, render.
	const rsosByID = useSelector(selectRSOsById);
	const rso = rsosByID[id];

	// If missing, fetch from back-end
	const dispatch = useDispatch();
	useEffect(() => {
		// We specifically check for null / undefined, as an RSO that is either
		// already busy loading, or is known to not exist, should have a non-null
		// representation in state already.
		// TODO: ...but we should re-try a non-404 failed fetch :-/
		if (!rso) {
			dispatch(fetchRSOById(id));
		}
	});

	if (!rso) {
		return (
			<>
				<Cell>-</Cell>
				<Cell>-</Cell>
				<Cell>-</Cell>
			</>
		);
	}

	if (rso._loadStatus == "loading") {
		return (
			<>
				<Cell colSpan={3}>
					<Text small active>
						Loading...
					</Text>
				</Cell>
			</>
		);
	}

	if (rso._loadStatus == "rejected") {
		return (
			<>
				<Cell colSpan={3}>
					<Text small error>
						No data for this RSO
					</Text>
				</Cell>
			</>
		);
	}
	return (
		<>
			<Cell onClick={onClick}>{rso.object_name}</Cell>
			<Cell onClick={onClick}>
				<ID>{rso.object_id}</ID>
			</Cell>
			<Cell onClick={onClick}>
				{calculateOrbitType(rso.periapsis, rso.apoapsis, rso.period)}
			</Cell>
		</>
	);
};

RSOMetaCols.propTypes = {
	id: PropTypes.number.isRequired,
	onClick: PropTypes.func,
};

const RSOIDInput = ({ value, onChange }) => {
	// String-typed 'buffer' representing what the user
	// typed. Once the user hits enter or blurs the field,
	// we parse a NORAD ID from the entered text and
	// send it upstream with `onChange`.
	const [bval, setBval] = useState("" + value);
	const propagateChange = () => {
		const ids = parseIDs(bval);
		if (ids.length > 0) {
			const newVal = ids[0];
			setBval("" + newVal); // Update display (sanitised valued)
			if (newVal != value) {
				onChange(newVal); // Pass upstream
			}
		}
	};
	return (
		<Input
			type="text"
			value={bval}
			onBlur={propagateChange}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					propagateChange();
				}
			}}
			onChange={(e) => setBval(e.target.value)}
		/>
	);
};

RSOIDInput.propTypes = {
	value: PropTypes.number.isRequired,
	onChange: PropTypes.func.isRequired,
};

export default PriorityList;
